'use client';

import ButtonKaraoke from '@/context/domain/g02/g02-d03/g02-d03-p001-karaoke/components/ButtonKaraoke';
import VolumeControl from '@/context/domain/g02/g02-d03/g02-d03-p001-karaoke/components/VolumeControl';
import IconPlay from '@/context/global/components/Icon/IconPlay';
import IconPause from '@/context/global/components/Icon/IconPause';
import IconPrevious from '@/context/global/components/Icon/IconPrevious';
import IconNext from '@/context/global/components/Icon/IconNext';
import IconRepeat from '@/context/global/components/Icon/IconRepeat';
import IconHeart from '@/context/global/components/Icon/IconHeart';
import IconFavorite from '@/context/global/components/Icon/IconFavorite';
import IconCallingMelody from '@/context/global/components/Icon/IconCallingMelody';
import IconMusicDisk from '@/context/global/components/Icon/IconMusicDisk';
import IconMicrophoneMute from '@/context/global/components/Icon/IconMicrophoneOff';
import IconMicrophoneOn from '@/context/global/components/Icon/IconMicrophoneOn';
import IconSearch from '@/context/global/components/Icon/IconSearch';
import { cn } from '@/context/global/utils/cn';
import { useMemo } from 'react';

type PlayerControllerProps = {
  title?: string;
  artist?: string;
  isPlaying: boolean;
  isRepeat: boolean;
  isFavorite: boolean;
  isVocalOn: boolean;
  volume: number;
  isPreview?: boolean;
  hideCallingMelody?: boolean;
  disabledNext?: boolean;
  forceMobile?: boolean;
  onPrevious?: () => void;
  onNext?: () => void;
  onTogglePlay: () => void;
  onToggleRepeat: () => void;
  onToggleVocal: () => void;
  onVolumeChange: (value: number) => void;
  onFavorite?: () => void;
  onMelody?: () => void;
  onToggleSearch?: () => void;
};

const PlayerController = ({
  title = 'Unknown Title',
  artist = 'Unknown Artist',
  isPlaying,
  isRepeat,
  isFavorite,
  isVocalOn,
  volume,
  hideCallingMelody,
  isPreview,
  disabledNext,
  onPrevious,
  onNext,
  onTogglePlay,
  onToggleRepeat,
  onToggleVocal,
  onVolumeChange,
  onFavorite,
  onMelody,
  onToggleSearch,
  forceMobile,
}: PlayerControllerProps) => {
  const isHideCallingMelody = useMemo(
    () => isPreview || hideCallingMelody,
    [hideCallingMelody, isPreview],
  );
  const isHideToggleVocal = useMemo(() => isPreview, [isPreview]);
  const isHideToggleSearch = useMemo(() => isPreview, [isPreview]);
  const isHideFavorite = useMemo(() => isPreview, [isPreview]);

  return (
    <>
      {/* Mobile: Top Right Actions */}
      <div
        className={cn(
          'pointer-events-auto absolute right-5 top-[10px] flex items-center gap-2 md:hidden',
          forceMobile && '!flex',
        )}
      >
        <ButtonKaraoke
          hidden={isHideCallingMelody}
          onClick={onMelody}
          className="flex items-center justify-center p-2 xl:p-0"
        >
          <IconCallingMelody size={16} />
        </ButtonKaraoke>

        <ButtonKaraoke
          hidden={isHideToggleVocal}
          onClick={onToggleVocal}
          className="flex items-center justify-center p-2 xl:p-0"
        >
          {isVocalOn ? (
            <IconMicrophoneOn size={16} className="text-white" />
          ) : (
            <IconMicrophoneMute size={16} className="text-white" />
          )}
        </ButtonKaraoke>

        <ButtonKaraoke
          hidden={isHideToggleSearch}
          onClick={onToggleSearch}
          className="flex items-center justify-center p-2 xl:p-0"
        >
          <IconSearch size={16} />
        </ButtonKaraoke>

        <ButtonKaraoke
          hidden={isHideFavorite}
          onClick={onFavorite}
          className="flex items-center justify-center p-2 md:p-0"
        >
          {isFavorite ? (
            <IconFavorite className="size-4 text-[#A3D13C]" />
          ) : (
            <IconHeart size={16} className="text-white" />
          )}
        </ButtonKaraoke>
      </div>

      {/* Mobile: Center Playback Controls - positioned in vertical & horizontal center */}
      <div
        className={cn(
          'pointer-events-auto absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center gap-4 md:hidden',
          forceMobile && '!flex',
        )}
      >
        <ButtonKaraoke hidden={isPreview} onClick={onPrevious} className="flex items-center justify-center">
          <IconPrevious size={16} className="text-white" />
        </ButtonKaraoke>

        <ButtonKaraoke
          hidden={isPreview && isPlaying}
          onClick={isPreview && isPlaying ? undefined : onTogglePlay}
          className="flex items-center justify-center"
        >
          {isPlaying ? (
            <IconPause size={16} className="text-white" />
          ) : (
            <IconPlay size={16} className="text-white" />
          )}
        </ButtonKaraoke>

        <ButtonKaraoke
          hidden={isPreview}
          onClick={onNext}
          disabled={disabledNext}
          className="flex items-center justify-center"
        >
          <IconNext size={16} className="text-white" />
        </ButtonKaraoke>
      </div>

      {/* Desktop: 3-column layout */}
      <div
        className={cn(
          'mx-8 hidden items-center justify-between md:flex',
          forceMobile && '!hidden',
        )}
      >
        {/* Left: Song Info */}
        <div className="flex min-w-0 flex-1 items-center gap-4">
          <div className="flex size-[50px] items-center justify-center rounded-lg">
            <IconMusicDisk size={50} className="text-black" />
          </div>
          <div className="flex min-w-0 flex-col">
            <span className="font-db-heavent truncate text-[30px] font-bold leading-tight text-white">
              {title}
            </span>
            <span className="font-db-heavent truncate text-[26px] text-[#B5B5B5]">
              {artist}
            </span>
          </div>
        </div>

        {/* Center: Playback Controls */}
        <div className="flex flex-1 items-center justify-center gap-4">
          <ButtonKaraoke
            hidden={isPreview}
            onClick={onPrevious}
            className="flex size-[72px] items-center justify-center"
          >
            <IconPrevious size={32} className="text-white" />
          </ButtonKaraoke>

          <ButtonKaraoke
            onClick={isPreview && isPlaying ? undefined : onTogglePlay}
            className="flex size-[72px] items-center justify-center"
          >
            {isPlaying ? (
              <IconPause size={32} className="text-white" />
            ) : (
              <IconPlay size={32} className="text-white" />
            )}
          </ButtonKaraoke>

          <ButtonKaraoke
            hidden={isPreview}
            onClick={onNext}
            disabled={disabledNext}
            className="flex size-[72px] items-center justify-center"
          >
            <IconNext size={32} className="text-white" />
          </ButtonKaraoke>
        </div>

        {/* Right: Additional Controls */}
        <div className="flex flex-1 items-center justify-end gap-4">
          <VolumeControl
            hidden={isPreview}
            volume={volume}
            onVolumeChange={onVolumeChange}
          />

          <ButtonKaraoke
            hidden
            onClick={onToggleRepeat}
            className="flex size-[72px] items-center justify-center"
          >
            <IconRepeat
              size={32}
              className={isRepeat ? 'text-[#A3D13C]' : 'text-white'}
            />
          </ButtonKaraoke>

          <ButtonKaraoke
            hidden={isHideToggleVocal}
            onClick={onToggleVocal}
            className="flex size-[72px] items-center justify-center"
          >
            {isVocalOn ? (
              <IconMicrophoneOn size={32} className="text-white" />
            ) : (
              <IconMicrophoneMute size={32} className="text-white" />
            )}
          </ButtonKaraoke>

          <ButtonKaraoke
            hidden={isHideFavorite}
            onClick={onFavorite}
            className="flex size-[72px] items-center justify-center"
          >
            {isFavorite ? (
              <IconFavorite className="size-8 text-[#A3D13C]" />
            ) : (
              <IconHeart size={32} className="text-white" />
            )}
          </ButtonKaraoke>

          <ButtonKaraoke
            hidden={isHideCallingMelody}
            onClick={onMelody}
            className="flex size-[72px] items-center justify-center"
          >
            <IconCallingMelody size={32} />
          </ButtonKaraoke>
        </div>
      </div>
    </>
  );
};

export default PlayerController;
